import { Alert, AlertDescription, Button, Checkbox, Input, Label, Spinner } from "@veritrellis/ui";
import { FormField } from "../components/FormField.js";
import { AuthShell } from "../components/auth/AuthShell.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { legalDocumentVersions, requiredLegalAcceptanceDocuments } from "@veritrellis/shared";
import { apiFetch } from "../lib/api.js";
import { marketingLegalUrl } from "../lib/marketingSite.js";
import type { WorkspaceRole } from "../hooks/useAdminMe.js";
import { writeWorkspaceIdToSession } from "../lib/workspaceStorage.js";

const slugFromName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .slice(0, 48);

type FormValues = {
  legalName: string;
  billingEmail: string;
  name: string;
  slug: string;
  acceptedBusinessTerms: boolean;
  acceptedSandboxDisclaimer: boolean;
};

type FormErrors = Partial<Record<keyof FormValues, string>>;

const validate = (values: FormValues): FormErrors => {
  const errors: FormErrors = {};
  if (values.legalName.trim().length === 0) errors.legalName = "Required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(values.billingEmail.trim())) {
    errors.billingEmail = "Valid email required";
  }
  if (values.name.trim().length === 0) errors.name = "Required";
  if (!/^[a-z0-9-]+$/u.test(values.slug)) {
    errors.slug = "Lowercase letters, numbers, and hyphens only";
  }
  if (values.slug.length < 2) {
    errors.slug = "At least 2 characters";
  }
  if (!values.acceptedBusinessTerms) {
    errors.acceptedBusinessTerms = "Required to create a workspace";
  }
  if (!values.acceptedSandboxDisclaimer) {
    errors.acceptedSandboxDisclaimer = "Required to create a workspace";
  }
  return errors;
};

const mapCreateError = (err: unknown): string => {
  if (!(err instanceof Error)) {
    return "Could not create workspace. Try again.";
  }
  if (err.message === "slug_taken") {
    return "That workspace slug is already taken. Choose another.";
  }
  if (err.message === "legal_acceptance_required") {
    return "Confirm the legal agreements below.";
  }
  return `Could not create workspace: ${err.message}`;
};

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const priceId = searchParams.get("price_id");
  const queryClient = useQueryClient();

  const [values, setValues] = useState<FormValues>({
    legalName: "",
    billingEmail: "",
    name: "",
    slug: "",
    acceptedBusinessTerms: false,
    acceptedSandboxDisclaimer: false
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [slugTouched, setSlugTouched] = useState(false);

  const meQ = useQuery({
    queryKey: ["admin", "me"],
    queryFn: async () => {
      const res = await apiFetch("/admin/me");
      if (!res.ok) {
        throw new Error("unauthorized");
      }
      return (await res.json()) as { user: { id: string; email: string } };
    }
  });

  useEffect(() => {
    const email = meQ.data?.user.email;
    if (email !== undefined && values.billingEmail.length === 0) {
      setValues((prev) => ({ ...prev, billingEmail: email }));
    }
  }, [meQ.data?.user.email, values.billingEmail.length]);

  const create = useMutation({
    mutationFn: async (formValues: FormValues) => {
      const acceptedDocuments = {
        ...requiredLegalAcceptanceDocuments,
        sandbox_disclaimer: legalDocumentVersions.sandbox_disclaimer
      };
      const res = await apiFetch("/admin/workspaces", {
        method: "POST",
        body: JSON.stringify({
          legal_name: formValues.legalName.trim(),
          billing_email: formValues.billingEmail.trim(),
          name: formValues.name.trim(),
          slug: formValues.slug.trim(),
          legal_acceptance: {
            business_terms_confirmed: formValues.acceptedBusinessTerms,
            sandbox_disclaimer_confirmed: formValues.acceptedSandboxDisclaimer,
            accepted_documents: acceptedDocuments
          }
        })
      });
      const payload = (await res.json().catch(() => ({}))) as {
        error?: { code?: string; message?: string };
        workspaceId?: string;
      };
      if (!res.ok) {
        const code = payload.error?.message ?? payload.error?.code ?? `http_${String(res.status)}`;
        throw new Error(code);
      }
      if (typeof payload.workspaceId !== "string" || payload.workspaceId.length === 0) {
        throw new Error("missing_workspace_id_in_response");
      }
      return {
        workspaceId: payload.workspaceId,
        name: formValues.name.trim(),
        slug: formValues.slug.trim()
      };
    },
    onSuccess: async (data) => {
      writeWorkspaceIdToSession(data.workspaceId);
      queryClient.setQueryData(["admin", "me"], (prev) => {
        if (prev === undefined || typeof prev !== "object") {
          return prev;
        }
        const p = prev as {
          user: { id: string; email: string };
          workspaces: Array<{ id: string; name: string; slug: string; role: WorkspaceRole }>;
          activeWorkspaceId: string | null;
        };
        const exists = p.workspaces.some((w) => w.id === data.workspaceId);
        const ownerRole: WorkspaceRole = "owner";
        const workspaces = exists
          ? p.workspaces
          : [
              ...p.workspaces,
              { id: data.workspaceId, name: data.name, slug: data.slug, role: ownerRole }
            ];
        return {
          ...p,
          workspaces,
          activeWorkspaceId: data.workspaceId
        };
      });
      await queryClient.invalidateQueries({ queryKey: ["admin", "me"] });
      if (priceId !== null && priceId.length > 0) {
        await navigate(`/checkout?price_id=${encodeURIComponent(priceId)}`, { replace: true });
        return;
      }
      await navigate("/dashboard", { replace: true });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    create.mutate(values);
  };

  if (meQ.isLoading) {
    return (
      <div className="admin-auth-loading">
        <Spinner />
      </div>
    );
  }

  const checkoutFlow = priceId !== null && priceId.length > 0;

  return (
    <AuthShell
      badge={checkoutFlow ? "Checkout" : "New workspace"}
      title="Create your workspace"
      description={
        checkoutFlow
          ? "Register your company, then continue to secure Stripe checkout."
          : "One workspace per company to start. You become the owner and can invite your team next."
      }
      userEmail={meQ.data?.user.email ?? null}
    >
      <form onSubmit={handleSubmit} className="admin-auth-card-body admin-form-stack">
        <section className="admin-auth-section" aria-labelledby="onboard-company-heading">
          <h2 id="onboard-company-heading" className="admin-auth-section-title">
            Company
          </h2>
          <FormField width="full">
            <Label htmlFor="legalName">Legal name</Label>
            <Input
              id="legalName"
              placeholder="Acme Corp Ltd."
              autoComplete="organization"
              required
              value={values.legalName}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setValues((prev) => {
                  const next = { ...prev, legalName: v };
                  if (!slugTouched && prev.slug.length === 0) {
                    next.slug = slugFromName(v);
                  }
                  if (prev.name.length === 0) {
                    next.name = v;
                  }
                  return next;
                });
              }}
            />
            {errors.legalName ? (
              <p className="text-xs text-destructive">{errors.legalName}</p>
            ) : null}
          </FormField>
          <FormField width="full">
            <Label htmlFor="billingEmail">Billing email</Label>
            <Input
              id="billingEmail"
              type="email"
              placeholder="billing@acme.com"
              autoComplete="email"
              required
              value={values.billingEmail}
              onChange={(e) => {
                setValues((prev) => ({ ...prev, billingEmail: e.currentTarget.value }));
              }}
            />
            {errors.billingEmail ? (
              <p className="text-xs text-destructive">{errors.billingEmail}</p>
            ) : null}
          </FormField>
        </section>

        <section className="admin-auth-section" aria-labelledby="onboard-workspace-heading">
          <h2 id="onboard-workspace-heading" className="admin-auth-section-title">
            Workspace
          </h2>
          <FormField width="full">
            <Label htmlFor="name">Display name</Label>
            <Input
              id="name"
              placeholder="Acme"
              required
              value={values.name}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setValues((prev) => {
                  const next = { ...prev, name: v };
                  if (!slugTouched) {
                    next.slug = slugFromName(v);
                  }
                  return next;
                });
              }}
            />
            {errors.name ? <p className="text-xs text-destructive">{errors.name}</p> : null}
          </FormField>
          <FormField width="full">
            <Label htmlFor="slug">URL slug</Label>
            <Input
              id="slug"
              placeholder="acme"
              autoComplete="off"
              spellCheck={false}
              required
              value={values.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setValues((prev) => ({
                  ...prev,
                  slug: e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/gu, "")
                }));
              }}
            />
            {values.slug.length > 0 ? (
              <p className="admin-auth-slug-preview">
                Workspace identifier: <strong>{values.slug}</strong>
              </p>
            ) : null}
            {errors.slug ? <p className="text-xs text-destructive">{errors.slug}</p> : null}
          </FormField>
        </section>

        <section className="admin-auth-section" aria-labelledby="onboard-legal-heading">
          <h2 id="onboard-legal-heading" className="admin-auth-section-title">
            Agreements
          </h2>
          <div className="admin-auth-legal-panel">
            <div className="admin-auth-checkbox-row">
              <Checkbox
                id="acceptedBusinessTerms"
                checked={values.acceptedBusinessTerms}
                onCheckedChange={(checked) =>
                  setValues((prev) => ({
                    ...prev,
                    acceptedBusinessTerms: checked === true
                  }))
                }
              />
              <label htmlFor="acceptedBusinessTerms" className="admin-auth-checkbox-label">
                I confirm this account is for business or professional use (not consumer), and I
                agree to the{" "}
                <a href={marketingLegalUrl("terms")} target="_blank" rel="noreferrer">
                  Terms of Service
                </a>
                ,{" "}
                <a href={marketingLegalUrl("privacy")} target="_blank" rel="noreferrer">
                  Privacy Notice
                </a>
                , and{" "}
                <a href={marketingLegalUrl("dpa")} target="_blank" rel="noreferrer">
                  DPA
                </a>{" "}
                where applicable.
              </label>
            </div>
            {errors.acceptedBusinessTerms ? (
              <p className="text-xs text-destructive" style={{ margin: "0 0 0.75rem 1.75rem" }}>
                {errors.acceptedBusinessTerms}
              </p>
            ) : null}

            <div className="admin-auth-checkbox-row">
              <Checkbox
                id="acceptedSandboxDisclaimer"
                checked={values.acceptedSandboxDisclaimer}
                onCheckedChange={(checked) =>
                  setValues((prev) => ({
                    ...prev,
                    acceptedSandboxDisclaimer: checked === true
                  }))
                }
              />
              <label htmlFor="acceptedSandboxDisclaimer" className="admin-auth-checkbox-label">
                I understand free-tier and sandbox environments are for evaluation only—not
                production, regulated, or highly sensitive data unless approved in writing.
              </label>
            </div>
            {errors.acceptedSandboxDisclaimer ? (
              <p className="text-xs text-destructive" style={{ margin: "0 0 0 1.75rem" }}>
                {errors.acceptedSandboxDisclaimer}
              </p>
            ) : null}
          </div>

          <Alert className="border-amber-200 bg-amber-50">
            <AlertDescription className="text-xs text-amber-900">
              Sandbox and free-tier data must stay non-production. Upgrade and contract terms apply
              before handling regulated workloads.
            </AlertDescription>
          </Alert>
        </section>

        <Button type="submit" disabled={create.isPending} className="w-full" size="lg">
          {create.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
          {checkoutFlow ? "Continue to checkout" : "Create workspace"}
        </Button>

        {create.isError ? (
          <div className="admin-auth-error-banner" role="alert">
            {mapCreateError(create.error)}
          </div>
        ) : null}
      </form>
    </AuthShell>
  );
};
